import React, { useCallback, useEffect, useState } from 'react';
import { addMonths, format, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { AlertTriangle, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { downloadCsv } from '../utils/csv';
import ProfessionalMonthlySummary from './ProfessionalMonthlySummary';

interface Payment { paid: true; paidAt: string; amount: number; note: string | null; changedSincePaid: boolean }
interface Row { professionalId: number; name: string; specialty: string | null; hours: number; baseCost: number; discount: number; owed: number; payment: Payment | null }
interface Overview { year: number; month: number; rows: Row[]; totals: { hours: number; owed: number; paid: number; pending: number } }

const money = (n: number) => `$${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(Math.round(n))}`;

/** Who rented rooms this month, what they owe, and who has paid. Replaces the old always-empty "ingresos estimados". */
const AdminRentalPayments: React.FC = () => {
  const [month, setMonth] = useState(new Date());
  const [data, setData] = useState<Overview | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [busy, setBusy] = useState<number | null>(null);
  const [detail, setDetail] = useState<number | null>(null);
  const year = month.getFullYear();
  const m = month.getMonth() + 1;

  const load = useCallback(async () => {
    setState('loading');
    try {
      const r = await api.get(`/admin/rental-payments/${year}/${m}`);
      setData(r.data);
      setState('ok');
    } catch {
      setState('error');
    }
  }, [year, m]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (row: Row, paid: boolean) => {
    if (!paid && !window.confirm(`¿Deshacer el pago de ${row.name}?`)) return;
    setBusy(row.professionalId);
    try {
      await api.put(`/admin/rental-payments/${row.professionalId}/${year}/${m}`, { paid });
      toast.success(paid ? `Pago de ${row.name} registrado` : 'Pago deshecho');
      await load();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo guardar el pago');
    } finally { setBusy(null); }
  };

  const exportCsv = () => {
    if (!data) return;
    downloadCsv(`alquileres-${year}-${String(m).padStart(2, '0')}.csv`, [
      ['Profesional', 'Especialidad', 'Horas', 'Costo base', 'Descuento', 'A pagar', 'Estado', 'Fecha de pago', 'Monto pagado'],
      ...data.rows.map(r => [r.name, r.specialty || '', r.hours, Math.round(r.baseCost), Math.round(r.discount), Math.round(r.owed),
        r.payment ? 'Pagado' : 'Pendiente', r.payment ? format(new Date(r.payment.paidAt), 'dd/MM/yyyy') : '', r.payment ? Math.round(r.payment.amount) : '']),
      ['TOTAL', '', data.totals.hours, '', '', Math.round(data.totals.owed), '', '', Math.round(data.totals.paid)],
    ]);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Alquileres y cobros</h2>
          <p className="mt-1 text-sm text-gray-500">Lo que cada profesional debe por los consultorios alquilados, y si ya pagó.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button className="btn btn-outline btn-md" aria-label="Mes anterior" onClick={() => setMonth(subMonths(month, 1))}><ChevronLeft className="h-4 w-4" /></button>
          <span className="min-w-[9rem] text-center font-semibold">{(s => s.charAt(0).toUpperCase() + s.slice(1))(format(month, 'MMMM yyyy', { locale: es }))}</span>
          <button className="btn btn-outline btn-md" aria-label="Mes siguiente" onClick={() => setMonth(addMonths(month, 1))}><ChevronRight className="h-4 w-4" /></button>
          <button className="btn btn-secondary btn-md" onClick={() => setMonth(new Date())}>Hoy</button>
          <button className="btn btn-outline btn-md" onClick={exportCsv} disabled={!data || data.rows.length === 0}><Download className="mr-1.5 h-4 w-4" />Exportar CSV</button>
        </div>
      </div>

      {state === 'loading' && <div className="animate-pulse rounded-2xl bg-gray-100 h-40" />}
      {state === 'error' && <div className="card p-8 text-center text-gray-500">No pudimos cargar los cobros. <button className="underline font-semibold" onClick={load}>Reintentar</button></div>}

      {state === 'ok' && data && (<>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-white p-4"><div className="text-2xl font-bold text-gray-900">{money(data.totals.owed)}</div><div className="text-sm text-gray-600">A cobrar en el mes</div></div>
          <div className="rounded-xl border border-green-200 bg-green-50 p-4"><div className="text-2xl font-bold text-green-700">{money(data.totals.paid)}</div><div className="text-sm text-green-700">Cobrado</div></div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4"><div className="text-2xl font-bold text-amber-800">{money(data.totals.pending)}</div><div className="text-sm text-amber-800">Pendiente</div></div>
        </div>

        {data.rows.length === 0 ? (
          <div className="card p-10 text-center text-gray-500">Nadie alquiló consultorios en este mes.</div>
        ) : (
          <div className="card overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">Profesional</th><th className="px-4 py-3 text-right">Horas</th>
                  <th className="px-4 py-3 text-right">Costo base</th><th className="px-4 py-3 text-right">Descuento</th>
                  <th className="px-4 py-3 text-right">A pagar</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {data.rows.map(r => (
                  <tr key={r.professionalId}>
                    <td className="px-4 py-3"><div className="font-medium text-gray-900">{r.name}</div><div className="text-xs text-gray-500">{r.specialty}</div></td>
                    <td className="px-4 py-3 text-right">{r.hours}</td>
                    <td className="px-4 py-3 text-right">{money(r.baseCost)}</td>
                    <td className="px-4 py-3 text-right text-green-700">{r.discount > 0 ? `-${money(r.discount)}` : '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold">{money(r.owed)}</td>
                    <td className="px-4 py-3">
                      {r.payment ? (
                        <div>
                          <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-bold text-green-800">Pagado {format(new Date(r.payment.paidAt), 'd/M')}</span>
                          {r.payment.changedSincePaid && (
                            <p className="mt-1 flex items-start gap-1 text-xs text-amber-800"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />Pagó {money(r.payment.amount)}, ahora debe {money(r.owed)}</p>
                          )}
                        </div>
                      ) : (
                        <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800">Pendiente</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button className="btn btn-sm btn-outline mr-2" onClick={() => setDetail(detail === r.professionalId ? null : r.professionalId)} aria-expanded={detail === r.professionalId}>
                        {detail === r.professionalId ? 'Ocultar detalle' : 'Ver detalle'}
                      </button>
                      {r.payment
                        ? <button className="btn btn-sm btn-outline" disabled={busy === r.professionalId} onClick={() => toggle(r, false)}>Deshacer</button>
                        : <button className="btn btn-sm btn-primary" disabled={busy === r.professionalId || r.owed <= 0} onClick={() => toggle(r, true)}>Marcar pagado</button>}
                      {r.payment?.changedSincePaid && <button className="btn btn-sm btn-primary ml-2" disabled={busy === r.professionalId} onClick={() => toggle(r, true)}>Actualizar monto</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 text-sm font-semibold">
                <tr><td className="px-4 py-3">Total</td><td className="px-4 py-3 text-right">{data.totals.hours}</td><td /><td /><td className="px-4 py-3 text-right">{money(data.totals.owed)}</td><td colSpan={2} /></tr>
              </tfoot>
            </table>
          </div>
        )}
        {detail !== null && data.rows.some(r => r.professionalId === detail) && (
          <div className="card p-5">
            <ProfessionalMonthlySummary key={`${detail}-${year}-${m}`} professionalId={String(detail)} fixedMonth={month} lockProfessional />
          </div>
        )}
      </>)}
    </div>
  );
};

export default AdminRentalPayments;
